import { FetchableResourceStatus } from '~/_shared/types/fetchableResource/fetchableResource';
import { isResourceLoaded } from '~/_shared/types/fetchableResource/fetchableResource.helpers';
import { compareSpreadsheetColumnIds } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import { ChartMetricType } from '../mapSettings/data/charts/mapSettingsCharts.types';
import { StatsProcessingStatus } from './stats.repository';
import type {
  BoundaryStatItemDemographic,
  BoundaryStatItemDemographicMetricData,
  BoundaryStatItemSpreadsheetColumn,
  BoundaryStatItemSpreadsheetColumnMetricData, BoundaryStatsItem, StatItem, StatItemMetricData,
  StatsState,
} from './stats.state';

export const isStatsItemLoading = (statsItem: StatItem): boolean => (
  statsItem.data.status === FetchableResourceStatus.Loading
  || statsItem.data.status === FetchableResourceStatus.Requested
);

export const isStatsItemLoaded = (statsItem: StatItem): boolean => (
  isResourceLoaded(statsItem.data)
);

export const isStatsItemSyncing = (statsItem: StatItem): boolean => (
  isResourceLoaded(statsItem.data)
  && statsItem.data.value.status !== StatsProcessingStatus.Finished
);

export const isBoundaryStatItemSpreadsheetColumn = (metricData: BoundaryStatsItem): metricData is BoundaryStatItemSpreadsheetColumn => (
  metricData.boundaryGroupId !== undefined && metricData.type === ChartMetricType.SpreadsheetColumn
);

export const isBoundaryStatItemDemographic = (metricData: BoundaryStatsItem): metricData is BoundaryStatItemDemographic => (
  metricData.boundaryGroupId !== undefined && metricData.type === ChartMetricType.Demographic
);

export const isBoundaryStatItemSpreadsheetColumnMetricData = (metricData: StatItemMetricData): metricData is BoundaryStatItemSpreadsheetColumnMetricData => (
  metricData.boundaryGroupId !== undefined && metricData.type === ChartMetricType.SpreadsheetColumn
);

export const isBoundaryStatItemDemographicMetricData = (metricData: StatItemMetricData): metricData is BoundaryStatItemDemographicMetricData => (
  metricData.boundaryGroupId !== undefined && metricData.type === ChartMetricType.Demographic
);

export const findBoundaryGroupStatItemSpreadsheetColumn = (
  boundaryGroupsStats: StatsState['boundaryGroupStats'],
  metricData: BoundaryStatItemSpreadsheetColumnMetricData,
): BoundaryStatItemSpreadsheetColumn | null => {
  const boundaryGroupStats = boundaryGroupsStats.get(metricData.boundaryGroupId);
  if (boundaryGroupStats) {
    return Array.from(boundaryGroupStats.values())
      .filter(isBoundaryStatItemSpreadsheetColumn)
      .find(stat => compareSpreadsheetColumnIds(stat.column, metricData.column)) ?? null;
  }
  return null;
};

export const findBoundaryGroupStatItemDemographic = (
  boundaryGroupsStats: StatsState['boundaryGroupStats'],
  metricData: BoundaryStatItemDemographicMetricData,
): BoundaryStatItemDemographic | null => {
  const boundaryGroupStats = boundaryGroupsStats.get(metricData.boundaryGroupId);
  if (boundaryGroupStats) {
    return Array.from(boundaryGroupStats.values())
      .filter(isBoundaryStatItemDemographic)
      .find(stat => stat.demographicId === metricData.demographicId) ?? null;
  }
  return null;
};
